import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Globe, MapPin, Lock, Clock, Search, Filter } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import Amenities from '../components/Amenities';
import { workspaceProducts, getAllProducts } from '../data/workspaceData';
import { Button } from '../components/ui/button';

// Icon mapping
const iconMap = {
  Building2,
  Users,
  Globe,
  MapPin,
  Lock,
  Clock
};

const AllProductsPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const allProducts = getAllProducts();
  const categories = Object.values(workspaceProducts);

  // Filter products based on selected category and search term
  const filteredProducts = allProducts.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleCategoryClick = (categoryId) => {
    navigate(`/${categoryId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Workspace Solutions
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Discover the perfect workspace solution for your business needs. From private offices to flexible coworking spaces, we have everything you need to grow and thrive.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-lg mx-auto relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search workspace solutions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e30613] focus:border-transparent"
              />
            </div>
          </div>

          {/* Category Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {categories.map((category) => {
              const IconComponent = iconMap[category.icon] || Building2;
              return (
                <div
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-300 cursor-pointer group hover:border-[#e30613]"
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-[#e30613] rounded-lg group-hover:bg-[#c1050f] transition-colors">
                      <IconComponent className="w-6 h-6 text-white" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#e30613] transition-colors">
                      {category.title}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    {category.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#e30613] font-semibold">
                      {category.products.length} options
                    </span>
                    <span className="text-[#e30613] text-sm font-medium group-hover:translate-x-1 transition-transform">
                      Explore →
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filters and Products Section */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Filter Tabs */}
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <div className="flex items-center space-x-2 text-gray-700">
            <Filter size={20} />
            <span className="font-medium">Filter by category:</span>
          </div>
          
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'bg-[#e30613] text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:border-[#e30613] hover:text-[#e30613]'
            }`}
          >
            All Products ({allProducts.length})
          </button>
          
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category.id
                  ? 'bg-[#e30613] text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:border-[#e30613] hover:text-[#e30613]'
              }`}
            >
              {category.title} ({category.products.length})
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <>
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {selectedCategory === 'all' 
                  ? `All Workspace Solutions (${filteredProducts.length})`
                  : `${workspaceProducts[selectedCategory]?.title} (${filteredProducts.length})`
                }
              </h2>
              <p className="text-gray-600">
                {searchTerm ? `Showing results for "${searchTerm}"` : 'Choose the perfect workspace solution for your needs'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
                <div key={`${product.category}-${product.id}`} className="relative">
                  {/* Category Badge */}
                  <div className="absolute -top-2 left-4 z-10 bg-[#e30613] text-white text-xs font-semibold px-3 py-1 rounded-full">
                    {product.categoryTitle}
                  </div>
                  <ProductCard 
                    product={product}
                    category={workspaceProducts[product.category]}
                    className="h-full pt-4"
                  />
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" strokeWidth={1} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No products found
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm 
                ? `No products match "${searchTerm}". Try adjusting your search or category filter.`
                : 'No products available in this category.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
                className="bg-[#e30613] hover:bg-[#c1050f]"
              >
                Clear Filters
              </Button>
              <Button 
                onClick={() => navigate('/contact')}
                variant="outline"
                className="border-[#e30613] text-[#e30613] hover:bg-[#e30613] hover:text-white"
              >
                Contact Us
              </Button>
            </div>
          </div>
        )}

        {/* Call to Action Section */}
        <div className="mt-16 bg-gradient-to-br from-[#e30613] to-[#c1050f] rounded-xl text-white p-8 text-center">
          <h3 className="text-3xl font-bold mb-4">
            Ready to Get Started?
          </h3>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Find the perfect workspace solution for your business. Our team is here to help you choose the right option and get you set up quickly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/contact')}
              className="bg-white text-[#e30613] hover:bg-gray-100 font-bold px-8 py-3"
            >
              Schedule a Tour
            </Button>
            <Button 
              onClick={() => navigate('/about')}
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#e30613] font-bold px-8 py-3"
            >
              Learn More
            </Button>
          </div>
        </div>
      </div>

      <Amenities title="Workspace Amenities" />

      <Footer />
    </div>
  );
};

export default AllProductsPage;